import type { MapViewMode } from './basemaps';
import type { FeatureOverlayKey, FeatureOverlayState, PoiTypeKey, PoiTypeState } from './mapLayers';
import { DEFAULT_POI_TYPE_STATE, ENTITY_TOGGLES, FEATURE_OVERLAYS, POI_TYPES } from './mapLayers';
import type { MapPreferences, MapViewport } from './mapPreferences';
import { DEFAULT_MAP_FILTER_STATE, type MapFilterState } from './mapFilters';
import { validateMapFilterState } from './mapFilterPersistence';

export interface MapSearchParams {
    client?: string;
    /** Serialised filter state: '0' = explicit inactive; 'v1:<json>' = active with diff payload; absent = use localStorage. */
    filters?: string;
    /**
     * Network Explorer's own committed filter state, in the same encoding as `filters`.
     * A separate param so a shared explorer link reproduces the explorer's filtered view
     * and nothing an explorer URL carries can ever read as a Find & Fix filter (or the
     * reverse); each mode writes and reads only its own.
     */
    explorerFilters?: string;
    lat?: number;
    lng?: number;
    z?: number;
    style?: string;
    view?: MapViewMode;
    /** Comma-separated visible layer state keys. Absent = use defaults; empty string = all off. */
    layers?: string;
    /**
     * Inbound only: a record to select on arrival (its wire entity id — `leak:`-prefixed for
     * leak entities) and the worklist it belongs to, as the Network Explorer's "Open in
     * Find & Fix" hand-off writes them. Never produced by the map's own URL writes, so the
     * first viewport write after arrival drops them.
     */
    poi?: string;
    group?: string;
}

export interface InitialMapState {
    selectedBasemapId: string;
    viewMode: MapViewMode;
    featureOverlays: FeatureOverlayState;
    poiTypes: PoiTypeState;
    initialViewport: MapViewport | undefined;
    /**
     * null  = filters param absent from URL — caller should use localStorage.
     * non-null = URL filter state present (active or explicitly inactive) — takes precedence over localStorage.
     */
    filterState: { isActive: boolean; committed: MapFilterState } | null;
}

export interface MapStateSnapshot {
    selectedBasemapId: string;
    viewMode: MapViewMode;
    featureOverlays: FeatureOverlayState;
    poiTypes: PoiTypeState;
    viewport: MapViewport | undefined;
    clientId: string | undefined;
    filterIsActive?: boolean;
    filterCommitted?: MapFilterState;
}

// -- Coordinate rounding --

export function roundCoord(n: number): number {
    return Math.round(n * 1e5) / 1e5;
}

export function roundZoom(n: number): number {
    return Math.round(n * 100) / 100;
}

// -- Coercion helpers (TanStack Router passes query values as strings) --

function coerceNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && isFinite(value)) return value;
    if (typeof value === 'string') {
        const n = parseFloat(value);
        if (isFinite(n)) return n;
    }
    return undefined;
}

function coerceLatitude(value: unknown): number | undefined {
    const n = coerceNumber(value);
    if (n === undefined || n < -90 || n > 90) return undefined;
    return n;
}

function coerceLongitude(value: unknown): number | undefined {
    const n = coerceNumber(value);
    if (n === undefined || n < -180 || n > 180) return undefined;
    return n;
}

function coerceZoom(value: unknown): number | undefined {
    const n = coerceNumber(value);
    if (n === undefined || n < 0 || n > 24) return undefined;
    return n;
}

function coerceViewMode(value: unknown): MapViewMode | undefined {
    if (value === '2d' || value === '3d' || value === 'globe') return value;
    return undefined;
}

/**
 * Validates and coerces raw query string values into typed MapSearchParams.
 * Used as `validateSearch` in TanStack Router route definitions.
 * Never throws — all invalid or missing values become `undefined`.
 */
export function validateMapSearch(raw: Record<string, unknown>): MapSearchParams {
    return {
        client: typeof raw.client === 'string' && raw.client.length > 0 ? raw.client : undefined,
        filters: typeof raw.filters === 'string' ? raw.filters : undefined,
        explorerFilters: typeof raw.explorerFilters === 'string' ? raw.explorerFilters : undefined,
        lat: coerceLatitude(raw.lat),
        lng: coerceLongitude(raw.lng),
        z: coerceZoom(raw.z),
        style: typeof raw.style === 'string' && raw.style.length > 0 ? raw.style : undefined,
        view: coerceViewMode(raw.view),
        layers: typeof raw.layers === 'string' ? raw.layers : undefined,
        poi: typeof raw.poi === 'string' && raw.poi.length > 0 ? raw.poi : undefined,
        group: typeof raw.group === 'string' && raw.group.length > 0 ? raw.group : undefined,
    };
}

// -- Filter serialisation --

/**
 * Serialises filter state for the URL `filters` search param.
 *
 * - Inactive → `'0'` (explicit marker; recipients see unfiltered map regardless of their localStorage).
 * - Active → `'v1:<diff-json>'` where only non-default fields are included.
 *
 * TanStack Router handles URL-encoding of the returned string; no manual encoding needed.
 */
export function serializeFiltersToUrl(isActive: boolean, committed: MapFilterState): string {
    if (!isActive) return '0';

    const payload: Record<string, unknown> = {};

    if (committed.show !== DEFAULT_MAP_FILTER_STATE.show) payload['show'] = committed.show;
    if (committed.dateRange !== DEFAULT_MAP_FILTER_STATE.dateRange) payload['dateRange'] = committed.dateRange;
    if (committed.customDateRange.from !== null || committed.customDateRange.to !== null) {
        payload['customDateRange'] = committed.customDateRange;
    }
    if (committed.leakVisibility !== DEFAULT_MAP_FILTER_STATE.leakVisibility) {
        payload['leakVisibility'] = committed.leakVisibility;
    }
    if (committed.noDigRepairs !== DEFAULT_MAP_FILTER_STATE.noDigRepairs) {
        payload['noDigRepairs'] = committed.noDigRepairs;
    }

    const leakTypesDiff: Record<string, boolean> = {};
    for (const key of Object.keys(DEFAULT_MAP_FILTER_STATE.leakTypes) as Array<
        keyof typeof DEFAULT_MAP_FILTER_STATE.leakTypes
    >) {
        if (!committed.leakTypes[key]) leakTypesDiff[key] = false;
    }
    if (Object.keys(leakTypesDiff).length > 0) payload['leakTypes'] = leakTypesDiff;

    const pipeTypesDiff: Record<string, boolean> = {};
    for (const key of Object.keys(DEFAULT_MAP_FILTER_STATE.pipeTypes) as Array<
        keyof typeof DEFAULT_MAP_FILTER_STATE.pipeTypes
    >) {
        if (!committed.pipeTypes[key]) pipeTypesDiff[key] = false;
    }
    if (Object.keys(pipeTypesDiff).length > 0) payload['pipeTypes'] = pipeTypesDiff;

    return `v1:${JSON.stringify(payload)}`;
}

/**
 * Parses the raw `filters` URL param string.
 *
 * Returns:
 * - `{ isActive: false, committed: defaults }` for `'0'` (explicit inactive marker).
 * - `{ isActive: true, committed: validated }` for a valid `'v1:<json>'` payload.
 * - `null` for unknown version prefixes or malformed JSON — caller should force inactive.
 *
 * Never throws. Does not accept `undefined`; the caller decides what to do when the param is absent.
 */
export function deserializeFiltersFromUrl(raw: string): { isActive: boolean; committed: MapFilterState } | null {
    if (raw === '0') return { isActive: false, committed: { ...DEFAULT_MAP_FILTER_STATE } };
    if (!raw.startsWith('v1:')) return null;
    try {
        const parsed: unknown = JSON.parse(raw.slice(3));
        return { isActive: true, committed: validateMapFilterState(parsed) };
    } catch {
        return null;
    }
}

// -- Layer serialisation --

const FEATURE_OVERLAY_KEYS: ReadonlyArray<FeatureOverlayKey> = FEATURE_OVERLAYS.map((d) => d.key);
const POI_TYPE_KEYS: ReadonlyArray<PoiTypeKey> = POI_TYPES.map((d) => d.key);
const ENTITY_TOGGLE_KEYS = new Set<string>(ENTITY_TOGGLES.map((d) => d.key));

/** Joins visible layer keys with commas. Returns empty string when no optional layers are on. */
export function serializeLayersToUrl(featureOverlays: FeatureOverlayState, poiTypes: PoiTypeState): string {
    const visible: string[] = [];
    for (const key of FEATURE_OVERLAY_KEYS) {
        if (featureOverlays[key]) visible.push(key);
    }
    for (const key of POI_TYPE_KEYS) {
        if (poiTypes[key]) visible.push(key);
    }
    return visible.join(',');
}

/**
 * Parses a comma-separated layer param. Unknown keys are ignored. Absent keys default to false.
 * The param being present (even as empty string) is authoritative — caller does not fall back
 * to defaults — EXCEPT for split-model URLs: an entity toggle token (pois/leaks) marks the
 * value as entity-era, where the legacy per-status tokens cannot exist, so poiTypes fall back
 * to their all-on defaults and only the overlay tokens are honoured. Without this rule (the
 * mirror of the entity codec's legacy-token rule) an entity-era URL would zero the legacy
 * poiTypes on every load — hiding every POI/cluster layer wherever that state still drives
 * rendering, including a rolled-back frontend.
 */
export function deserializeLayersFromUrl(param: string): {
    featureOverlays: FeatureOverlayState;
    poiTypes: PoiTypeState;
} {
    const parts = new Set(
        param
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
    );

    const featureOverlays = Object.fromEntries(
        FEATURE_OVERLAY_KEYS.map((key) => [key, parts.has(key)]),
    ) as FeatureOverlayState;

    const isEntityEraUrl = [...parts].some((part) => ENTITY_TOGGLE_KEYS.has(part));
    const poiTypes = isEntityEraUrl
        ? { ...DEFAULT_POI_TYPE_STATE }
        : (Object.fromEntries(POI_TYPE_KEYS.map((key) => [key, parts.has(key)])) as PoiTypeState);

    return { featureOverlays, poiTypes };
}

// -- Initial state from URL --

/**
 * Merges validated URL search params onto loaded localStorage prefs to produce the
 * initial map state. URL params win when present and valid; prefs fill the rest.
 * Call this before useState initialisers so the values are available synchronously.
 */
export function buildInitialMapStateFromSearch(
    search: MapSearchParams,
    prefs: MapPreferences,
    basemapIds: string[],
): InitialMapState {
    const selectedBasemapId =
        search.style && basemapIds.includes(search.style) ? search.style : prefs.selectedBasemapId;

    const viewMode = search.view ?? prefs.viewMode;

    const { featureOverlays, poiTypes } =
        search.layers !== undefined
            ? deserializeLayersFromUrl(search.layers)
            : { featureOverlays: prefs.featureOverlays, poiTypes: prefs.poiTypes };

    let initialViewport: MapViewport | undefined;
    if (search.lat !== undefined && search.lng !== undefined && search.z !== undefined) {
        // Apply view-mode appropriate pitch/bearing so the camera starts correctly
        // without relying on the post-load animation (which is skipped on first render).
        initialViewport = {
            latitude: search.lat,
            longitude: search.lng,
            zoom: search.z,
            pitch: viewMode === '3d' ? 60 : 0,
            bearing: viewMode === '3d' ? 20 : 0,
        };
    } else {
        initialViewport = prefs.viewport;
    }

    // filters param absent → null (caller uses localStorage).
    // filters param present but invalid → force inactive (do not fall back to recipient's localStorage).
    const filterState =
        search.filters !== undefined
            ? (deserializeFiltersFromUrl(search.filters) ?? {
                  isActive: false,
                  committed: { ...DEFAULT_MAP_FILTER_STATE },
              })
            : null;

    return { selectedBasemapId, viewMode, featureOverlays, poiTypes, initialViewport, filterState };
}

// -- Snapshot → URL params --

/** Builds the MapSearchParams object from current map state ready for navigate(). */
export function buildMapSearchParams(state: MapStateSnapshot): MapSearchParams {
    return {
        client: state.clientId,
        filters:
            state.filterIsActive !== undefined
                ? serializeFiltersToUrl(state.filterIsActive, state.filterCommitted ?? DEFAULT_MAP_FILTER_STATE)
                : undefined,
        lat: state.viewport !== undefined ? roundCoord(state.viewport.latitude) : undefined,
        lng: state.viewport !== undefined ? roundCoord(state.viewport.longitude) : undefined,
        z: state.viewport !== undefined ? roundZoom(state.viewport.zoom) : undefined,
        style: state.selectedBasemapId,
        view: state.viewMode,
        layers: serializeLayersToUrl(state.featureOverlays, state.poiTypes),
    };
}

// Sorted key order used by canonicaliseSearch — stable regardless of object creation order.
const CANON_KEYS = ['client', 'explorerFilters', 'filters', 'lat', 'layers', 'lng', 'style', 'view', 'z'] as const;

/**
 * Produces a stable string representation of MapSearchParams for equality comparison.
 * Omits undefined values, sorts keys, and rounds numeric fields to prevent floating-point
 * drift from causing false "external change" detections in the URL sync loop prevention.
 */
export function canonicaliseSearch(params: MapSearchParams): string {
    const out: Record<string, unknown> = {};
    for (const key of CANON_KEYS) {
        const val = params[key];
        if (val === undefined) continue;
        if (key === 'lat' || key === 'lng') {
            out[key] = roundCoord(val as number);
        } else if (key === 'z') {
            out[key] = roundZoom(val as number);
        } else {
            out[key] = val;
        }
    }
    return JSON.stringify(out);
}
